(function () {
  'use strict';

  // Legacy classic Product Options: the app has no event bus and renders no
  // running total on the PDP (only per-value "(+$50)" surcharge labels), so the
  // product order bar price never reflects the selected options. The modern
  // generation gets a live total via APO's `applyAttributeStateToHtml::price`
  // callback (see blocks/apo-options.liquid + snippets/mws-apo-helper.liquid);
  // legacy has no equivalent. This module reconstructs the total ourselves —
  // base variant price + the sum of the selected options' surcharges, read from
  // the app's own config global — and writes it into the order-bar price(s),
  // including the sticky scroll clone. Self-contained and loaded only on legacy
  // pages (from snippets/apo-legacy-objects.liquid); it never touches the
  // live-on-bont.com modern engine.

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var POLL_INTERVAL = 400;
  var MAX_ATTEMPTS = 20;

  /** The app config's option map, or null when the app has not booted yet. */
  function optionsConfig() {
    try {
      return window.mwProductOptions.Module.config.options || null;
    } catch (e) {
      return null;
    }
  }

  /** Resolve a value's { price, type } surcharge from the app config, or null. */
  function valuePricing(valueId) {
    if (!valueId) return null;
    var options = optionsConfig();
    if (!options) return null;
    for (var key in options) {
      var values = options[key] && options[key].values;
      var value = values && values[valueId];
      if (value) {
        return {
          price: parseFloat(value.price) || 0,
          type: value.price_type || 'fixed'
        };
      }
    }
    return null;
  }

  /** Current variant price in MAJOR units (dollars), from the bridge, or null.
   *  NOTE: `{{ product | json }}` serialises variant.price in the store's major
   *  currency units (e.g. 1529 for $1,529.00) — unlike the /products/x.js AJAX
   *  API, which uses cents. Surcharges from the app config are in the same major
   *  units, so the whole calculation runs in dollars and converts to cents only
   *  at the formatting step. */
  function baseAmount() {
    var product =
      window.mwProductOptionsObjects && window.mwProductOptionsObjects.product;
    var variants = product && product.variants;
    if (!variants || !variants.length) return null;

    // The APO options form keeps the hidden [name="id"] in sync with the picker.
    var input = document.querySelector('.apo-options-block input[name="id"]');
    var id = input ? parseInt(input.value, 10) : NaN;

    var chosen = null;
    if (!isNaN(id)) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) {
          chosen = variants[i];
          break;
        }
      }
    }
    if (!chosen) {
      // Fall back to the first available variant, else the first variant.
      for (var j = 0; j < variants.length; j++) {
        if (variants[j].available) {
          chosen = variants[j];
          break;
        }
      }
      if (!chosen) chosen = variants[0];
    }
    var price = typeof chosen.price === 'number' ? chosen.price : parseFloat(chosen.price);
    return isNaN(price) ? null : price;
  }

  /** Sum the selected options' surcharges (in dollars) across visible selects. */
  function surchargeAmount(base) {
    var total = 0;
    var selects = document.querySelectorAll('.mw-options-container select');
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      // Skip conditional options the app has hidden (display:none → no layout box).
      var block = select.closest('.mw-option');
      if (block && block.offsetParent === null) continue;

      var opt = select.options[select.selectedIndex];
      var valueId = opt ? opt.getAttribute('data-value-id') : '';
      var pricing = valuePricing(valueId);
      if (!pricing || !pricing.price) continue;

      if (pricing.type === 'percent') {
        total += base * (pricing.price / 100);
      } else {
        total += pricing.price;
      }
    }
    return total;
  }

  /** True when the storefront shows the currency code on product pages (theme
   *  setting). The order-bar total then uses money_with_currency_format so it
   *  matches the server render (money_with_currency), rather than the bare
   *  money_format. */
  function currencyCodeEnabled() {
    return !!(
      window.mwProductOptionsObjects &&
      window.mwProductOptionsObjects.currency_code_enabled_product_pages
    );
  }

  /** Shopify's standard money formatter, driven by the shop's money format. Uses
   *  the with-currency format when the currency-code setting is on. */
  function formatMoney(cents) {
    var shop =
      (window.mwProductOptionsObjects && window.mwProductOptionsObjects.shop) || {};
    var format =
      (currencyCodeEnabled() ? shop.money_with_currency_format : shop.money_format) ||
      shop.money_format ||
      '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var match = format.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    // Strip any HTML in the money format — the order-bar price is set via textContent.
    return format.replace(placeholder, value).replace(/<[^>]*>/g, '').trim();
  }

  /** Compute base + surcharges and write it into every order-bar price node. */
  function writeTotal() {
    var targets = document.querySelectorAll('.product-order-bar__price');
    if (!targets.length) return;
    if (!optionsConfig()) return; // app not booted yet — leave the server price

    var base = baseAmount();
    if (base == null || isNaN(base)) return;

    // base + surcharges are in dollars; formatMoney expects cents.
    var text = formatMoney(Math.round((base + surchargeAmount(base)) * 100));
    for (var i = 0; i < targets.length; i++) {
      targets[i].textContent = text;
      // Reveal the price once the real total is in — drop the skeleton the order
      // bar rendered for APO-priced products (matches the modern relay's mirror).
      var block = targets[i].closest('.product-order-bar__price-block');
      if (block) {
        block.classList.remove('product-order-bar__price-block--apo-pending');
        block.removeAttribute('data-pob-apo-pending');
      }
    }
  }

  function start(container) {
    // Recompute when the shopper changes any option.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('.mw-options-container select')) writeTotal();
    });

    // The app re-renders options on its conditional logic (show/hide, reselect);
    // recompute after it settles. writeTotal only writes OUTSIDE the container,
    // so this never feeds back into the observer.
    if (typeof MutationObserver !== 'undefined') {
      var timer;
      var observer = new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(writeTotal, 60);
      });
      observer.observe(container, { childList: true, subtree: true, attributes: true });
    }

    // Variant change swaps the order bar's inner HTML back to the server-rendered
    // base price, so recompute after the theme has re-rendered it.
    document.addEventListener('variant:update', function () {
      [0, 100, 400].forEach(function (delay) {
        setTimeout(writeTotal, delay);
      });
    });

    // Initial paint, plus a few safety re-syncs while APO finishes booting.
    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      writeTotal();
      if ((optionsConfig() && attempts >= 3) || attempts >= 12) clearInterval(poll);
    }, 500);
  }

  function init() {
    // The legacy mount is in the initial DOM on APO product pages; bail elsewhere
    // (this file loads site-wide on legacy stores).
    if (!document.querySelector('.mw-product-options')) return;

    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      var container = document.querySelector('.mw-options-container');
      if (container) {
        clearInterval(poll);
        start(container);
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(poll);
      }
    }, POLL_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
